<template>
  <div>
    <b-card no-body>
      <b-card-body>
        <validation-observer ref="refFormObserver">
          <b-form>
            <div class="d-flex justify-content-between align-items-center mb-2">
              <h5 class="mb-0 ">Users</h5>
              <div class="d-flex gap-1">
                <b-button variant="primary" size="sm" @click="addNewUser" :disabled="isLoadingUsers || isSyncingUsers"
                  class="text-capitalize">
                  <feather-icon icon="PlusIcon" size="14" class="mr-25" />
                  {{ $t("dmp.users.addUser") }}
                </b-button>
              </div>
            </div>

            <div v-if="isLoadingUsers" class="text-center py-4">
              <b-spinner variant="primary" :label="$t('dmp.users.loadingUsers')"></b-spinner>
              <p class="mt-2 mb-0">{{ $t("dmp.users.loadingUsers") }}</p>
            </div>

            <b-row v-if="!isLoadingUsers">
              <b-col cols="7" class="table-column">
                <!-- Users Table -->
                <b-table :items="users" :fields="userTableFields" responsive striped hover show-empty
                  :empty-text="$t('dmp.users.noUsersConfigured')" selectable select-mode="single"
                  @row-selected="onRowSelected" sort-by="number" :sort-desc="false" class="mb-0">
                  <template #cell(number)="data">
                    {{ parseInt(data.item.userNumber) }}
                  </template>

                  <template #cell(code)="data">
                    {{ data.item.userCode }}
                  </template>

                  <template #cell(name)="data">
                    {{ data.item.userName }}
                  </template>

                  <template #cell(status)="data">
                    <b-badge :variant="data.item.activeUser ? 'light-success' : 'light-danger'">
                      {{
                        data.item.activeUser
                          ? $t("dmp.users.active")
                          : $t("dmp.users.inactive")
                      }}
                    </b-badge>
                  </template>

                  <template #cell(profiles)="data">
                    <div v-if="data.item.profiles && data.item.profiles.length">
                      <span v-for="(profile, index) in data.item.profiles.filter(
                        (p) => p && p.trim()
                      )" :key="index" class="mr-25 mb-25">
                        {{ profile }}
                      </span>
                    </div>
                    <span v-else class="text-muted">{{
                      $t("dmp.users.noProfiles")
                      }}</span>
                  </template>
                  <template #cell(link)="data">
                    <b-button variant="primary" size="sm" v-b-tooltip.hover title="Enviar código"
                      @click="openModalUserCode(data.item)">
                      <feather-icon icon="SendIcon" />
                    </b-button>
                  </template>
                </b-table>
              </b-col>

              <b-col cols="5" class="details-column">
                <!-- Selected User Details Card -->
                <b-card v-if="selectedUser" no-body>
                  <b-card-header class="mb-1 card-header-custom pt-0">
                    <h6 class="mb-0">{{ $t("dmp.users.userDetails") }}</h6>
                  </b-card-header>
                  <b-card-body>
                    <validation-observer ref="userFormObserver">
                      <b-form @submit.prevent="saveUser">
                        <b-row>
                          <b-col cols="12" md="6">
                            <validation-provider #default="validationContext" :name="$t('dmp.users.userNumber')"
                              rules="required|integer|min_value:1|max_value:9999">
                              <b-form-group :label="$t('dmp.users.userNumber')" label-for="user-number">
                                <b-form-input id="user-number" v-model="selectedUser.userNumber"
                                  :state="getValidationState(validationContext)" maxlength="4" placeholder="0001"
                                  :disabled="isEditingUser" />
                                <b-form-text class="text-muted">
                                  <small>{{ $t("dmp.users.range") }}</small>
                                </b-form-text>
                                <b-form-invalid-feedback>
                                  {{ validationContext.errors[0] }}
                                </b-form-invalid-feedback>
                              </b-form-group>
                            </validation-provider>
                          </b-col>

                          <b-col cols="12" md="6">
                            <validation-provider #default="validationContext" :name="$t('dmp.users.userCode')"
                              rules="required|max:12">
                              <b-form-group :label="$t('dmp.users.userCode')" label-for="user-code">
                                <b-input-group>
                                  <b-form-input id="user-code" v-model="selectedUser.userCode"
                                    :state="getValidationState(validationContext)" maxlength="12" />
                                  <b-input-group-append>
                                    <b-button variant="outline-primary" class="btn-generate-code" v-b-tooltip.hover
                                      title="Generate random code" @click="generateRandomCode">
                                      <feather-icon icon="RefreshCwIcon" size="14" />
                                    </b-button>
                                  </b-input-group-append>
                                </b-input-group>
                                <b-form-text class="text-muted">
                                  <small>{{ $t("dmp.users.userCodeHint") }}</small>
                                </b-form-text>
                                <b-form-invalid-feedback>
                                  {{ validationContext.errors[0] }}
                                </b-form-invalid-feedback>
                              </b-form-group>
                            </validation-provider>
                          </b-col>

                          <b-col cols="12" md="6">
                            <validation-provider #default="validationContext" :name="$t('dmp.users.userPin')"
                              rules="integer|digits:6">
                              <b-form-group :label="$t('dmp.users.userPin')" label-for="user-pin" v-if="showPinField">
                                <b-input-group>
                                  <b-form-input id="user-pin" v-model.number="selectedUser.pinCode" type="number"
                                    :state="getValidationState(validationContext)" maxlength="6" />
                                  <b-input-group-append>
                                    <b-button variant="outline-primary" class="btn-generate-code" v-b-tooltip.hover
                                      title="Generate random PIN" @click="generateRandomPin">
                                      <feather-icon icon="RefreshCwIcon" size="14" />
                                    </b-button>
                                  </b-input-group-append>
                                </b-input-group>
                                <b-form-invalid-feedback>
                                  {{ validationContext.errors[0] }}
                                </b-form-invalid-feedback>
                              </b-form-group>
                            </validation-provider>
                          </b-col>

                          <b-col cols="12" md="6">
                            <validation-provider #default="validationContext" :name="$t('dmp.users.userName')"
                              rules="required|max:20">
                              <b-form-group :label="$t('dmp.users.userName')" label-for="user-name">
                                <b-form-input id="user-name" v-model="selectedUser.userName"
                                  :state="getValidationState(validationContext)" maxlength="20" placeholder="USER NAME"
                                  @input="
                                    selectedUser.userName =
                                    selectedUser.userName.toUpperCase()
                                    " />
                                <b-form-invalid-feedback>
                                  {{ validationContext.errors[0] }}
                                </b-form-invalid-feedback>
                              </b-form-group>
                            </validation-provider>
                          </b-col>

                          <b-col cols="12" md="6">
                            <b-form-group :label="$t('dmp.users.activeUser')" label-for="active-user">
                              <b-form-checkbox id="active-user" v-model="selectedUser.activeUser" switch>
                                {{
                                  selectedUser.activeUser
                                    ? $t("dmp.users.active")
                                    : $t("dmp.users.inactive")
                                }}
                              </b-form-checkbox>
                            </b-form-group>
                          </b-col>

                          <b-col cols="12">
                            <b-form-group :label="$t('dmp.users.profiles')">
                              <b-row>
                                <b-col cols="12" md="6" v-for="index in 4" :key="index - 1">
                                  <b-form-group :label="`${$t('dmp.users.profile')} ${index}`"
                                    :label-for="`user-profile-${index - 1}`">
                                    <b-form-select :id="`user-profile-${index - 1}`"
                                      v-model="selectedUser.profiles[index - 1]" :options="profileSelectOptions" />
                                  </b-form-group>
                                </b-col>
                              </b-row>
                            </b-form-group>
                          </b-col>

                          <b-col cols="12">
                            <validation-provider #default="validationContext"
                              :name="$t('dmp.outputInformation.description')" :rules="{ max: 200 }">
                              <b-form-group :label="$t('dmp.outputInformation.description')"
                                label-for="userDescription">
                                <b-form-textarea id="userDescription" v-model="selectedUser.description"
                                  :state="getValidationState(validationContext)"
                                  :placeholder="$t('dmp.outputInformation.description')" maxlength="200" rows="2" />
                                <b-form-invalid-feedback>
                                  {{ validationContext.errors[0] }}
                                </b-form-invalid-feedback>
                              </b-form-group>
                            </validation-provider>
                          </b-col>

                          <!-- Action Buttons -->
                          <b-col cols="12" class="d-flex justify-content-end mt-2">
                            <b-button v-if="isEditingUser" variant="outline-danger" size="sm" class="mr-1"
                              @click="deleteUser" :disabled="isSavingUser || isDeletingUser">
                              <b-spinner v-if="isDeletingUser" small class="mr-25" />
                              <feather-icon v-else icon="TrashIcon" size="14" class="mr-25" />
                              <span>{{ $t("actions.delete") }}</span>
                            </b-button>
                            <b-button variant="primary" size="sm" type="submit"
                              :disabled="isSavingUser || isDeletingUser">
                              <b-spinner v-if="isSavingUser" small class="mr-25" />
                              <feather-icon v-else icon="SaveIcon" size="14" class="mr-25" />
                              <span>{{
                                isSavingUser ? $t("actions.saving") : $t("actions.save")
                                }}</span>
                            </b-button>
                          </b-col>
                        </b-row>
                      </b-form>
                    </validation-observer>
                  </b-card-body>
                </b-card>
              </b-col>
            </b-row>
          </b-form>
        </validation-observer>
      </b-card-body>
    </b-card>
    <user-code-modal :show-modal-user-code="showModalUserCode" :user="userSelected"
      @update:isActive="closeModalUserCode()" />
  </div>
</template>

<script>
import { ref, computed, getCurrentInstance, watch } from "@vue/composition-api";
import { ValidationObserver, ValidationProvider } from "vee-validate";
import { useUtils as useI18nUtils } from "@core/libs/i18n";
import { useToast } from "vue-toastification/composition";
import formValidation from "@core/comp-functions/forms/form-validation";
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";
import store from "@/store";
import Swal from "sweetalert2";
import { formatToThreeDigits, formatToFourDigits } from "./usePanelConfig";
import UserCodeModal from "./UserCodeModal.vue";
import { useTemplateMode } from "./useTemplateMode";

export default {
  name: "DmpPanelsConfigUsers",
  components: {
    ValidationObserver,
    ValidationProvider,
    ToastificationContent,
    UserCodeModal
  },
  props: {
    uuid: {
      type: String,
      required: true,
    },
    usersData: {
      type: Array,
      default: () => [],
    },
    usersConfig: {
      type: Object,
      default: () => {},
    },
    profilesData: {
      type: Array,
      default: () => [],
    },
    panelId: {
      type: [Number, String],
      required: true,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    isActive: {
      type: Boolean,
      default: false,
    },
    source: {
      type: String,
      default: "default",
    },
  },
  setup(props, { emit }) {
    const vm = getCurrentInstance();
    const proxy = vm.root.proxy;

    const { t, tc } = useI18nUtils();
    const toast = useToast();
    const { refFormObserver, getValidationState } = formValidation();
    const { isTemplateMode, emitTemplateUpdate, nextSequentialId } = useTemplateMode(props, emit);

    const isLoadingUsers = computed(() => props.loading);
    const isSavingUser = ref(false);
    const isDeletingUser = ref(false);
    const isSyncingUsers = ref(false);
    const users = ref([]);

    const userFormObserver = ref(null);
    const isEditingUser = ref(false);
    const editingUserIndex = ref(-1);
    const selectedUser = ref(null);
    const showModalUserCode = ref(false);
    const userSelected = ref(null);

    // Los perfiles disponibles vienen siempre del panel/template ya cargado
    // (props.profilesData = p.profiles), sin fetch externo: funciona igual en
    // modo default y en modo template.
    const profiles = computed(() => props.profilesData || []);

    const profileSelectOptions = computed(() => {
      const options = [{ value: "", text: proxy.$t("dmp.users.noProfile") }];
      const source = profiles.value;
      if (!source || !source.length) return options;

      source.forEach((item) => {
        const profile = item.profile || item;
        options.push({
          value: String(profile.profileNumber),
          text: `${parseInt(profile.profileNumber)} - ${profile.profileName}`,
        });
      });

      return options;
    });

    const showPinField = computed(() => {
      if (!selectedUser.value || !profiles.value.length) return false;
      const assignedNumbers = (selectedUser.value.profiles || [])
        .filter((p) => p && p.trim())
        .map((p) => p.trim());
      if (!assignedNumbers.length) return false;

      return profiles.value.some((item) => {
        const profile = item.profile || item;
        const profileNum = String(profile.profileNumber).replace(/^0+/, "");
        return assignedNumbers.some((n) => n.replace(/^0+/, "") === profileNum) &&
          (profile.permissions?.cardPlusPin || profile.permissions?.cardPlusPIN);
      });
    });

    const userTableFields = computed(() => [
      { key: "number", label: t("dmp.users.userNumber"), sortable: true },
      { key: "code", label: t("dmp.users.userCode"), sortable: true },
      { key: "name", label: t("dmp.users.userName"), sortable: true },
      { key: "status", label: t("dmp.users.status") },
      { key: "profiles", label: t("dmp.users.profiles") },
      { key: "link", label: tc("actions.name", 2) }
    ]);

    const addNewUser = () => {
      isEditingUser.value = false;
      editingUserIndex.value = -1;
      selectedUser.value = {
        userNumber: null,
        userCode: "",
        userName: "",
        pinCode: null,
        activeUser: true,
        profiles: ["", "", "", ""],
        description: "",
      };
    };

    const onRowSelected = (items) => {
      if (items.length > 0) {
        const selected = items[0];

        let description = selected.description || "";
        if (isTemplateMode.value && props.usersConfig.users) {
          const configItem = props.usersConfig.users.find(
            (c) => parseInt(c.id) === parseInt(selected.userNumber)
          );

          description = configItem ? (configItem.description || "") : "";
        }

        isEditingUser.value = true;
        editingUserIndex.value = users.value.findIndex(
          (u) => u.userNumber === selected.userNumber
        );
        selectedUser.value = {
          userNumber: parseInt(selected.userNumber),
          userCode: selected.userCode || "",
          userName: selected.userName || "",
          pinCode: selected.pinCode || null,
          activeUser: selected.activeUser === true || selected.activeUser === "Y",
          profiles: [...(selected.profiles || ["", "", "", ""])],
          description,
        };

        if (!selectedUser.value.profiles) {
          selectedUser.value.profiles = ["", "", "", ""];
        } else {
          while (selectedUser.value.profiles.length < 4) {
            selectedUser.value.profiles.push("");
          }
          selectedUser.value.profiles = selectedUser.value.profiles.slice(0, 4);
        }
      } else {
        selectedUser.value = null;
        isEditingUser.value = false;
      }
    };

    const deleteUser = async () => {
      if (!isEditingUser.value) {
        return;
      }

      const result = await Swal.fire({
        title: proxy.$t("messages.delete.title"),
        text: proxy.$t("messages.delete.body"),
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: proxy.$t("actions.delete"),
        cancelButtonText: proxy.$t("actions.cancel"),
        customClass: {
          confirmButton: "btn btn-primary text-capitalize",
          cancelButton: "btn btn-outline-danger ml-1",
        },
        buttonsStyling: false,
      });

      if (!result.isConfirmed) {
        return;
      }

      if (isTemplateMode.value) {
        const list = (props.usersData || []).filter(
          (u) => parseInt(u.userNumber) !== parseInt(selectedUser.value.userNumber)
        );
        
        const configList = (props.usersConfig.users || []).filter(
          (c) => parseInt(c.id) !== parseInt(selectedUser.value.userNumber)
        );

        users.value = list;

        emit("template-update", {
          users: list,
          config: { ...props.usersConfig, users: configList },
        });

        toast({
          component: ToastificationContent,
          props: {
            title: proxy.$t("dmp.common.success"),
            text: proxy.$t("dmp.users.messages.userDeleteSuccess"),
            icon: "CheckIcon",
            variant: "success",
          },
        });
        selectedUser.value = null;
        isEditingUser.value = false;
        return;
      }

      isDeletingUser.value = true;
      store.commit('dmp_panels/setOperationActive', true);
      try {
        const userToDelete = selectedUser.value;

        const response = await store.dispatch("dmp_panels/deleteUser", {
          uuid: props.uuid,
          userNumber: formatToFourDigits(userToDelete.userNumber),
        });

        if (response.success) {
          emit("operation-completed");

          toast({
            component: ToastificationContent,
            props: {
              title: proxy.$t("dmp.common.success"),
              text: proxy.$t("dmp.users.messages.userDeleteSuccess"),
              icon: "CheckIcon",
              variant: "success",
            },
          });
          selectedUser.value = null;
          isEditingUser.value = false;
        } else {
          throw new Error(response.message || "Failed to delete user");
        }
      } catch (error) {
        console.error("Error deleting user:", error);
        emit("operation-error", error);

        // Extraer mensaje específico de la API
        const apiMessage =
          (error.response && error.response.data && error.response.data.message) ||
          error.message ||
          proxy.$t("dmp.users.messages.userDeleteError");

        toast({
          component: ToastificationContent,
          props: {
            title: proxy.$t("dmp.common.error"),
            text: apiMessage,
            icon: "AlertTriangleIcon",
            variant: "danger",
          },
        });
      } finally {
        isDeletingUser.value = false;
        store.commit('dmp_panels/setOperationActive', false);
      }
    };

    const saveUser = async (event) => {
      const isValid = await userFormObserver.value.validate();
      if (!isValid) {
        return;
      }

      const userCodeRegex = /^[0-9a-z]+$/;
      if (!userCodeRegex.test(selectedUser.value.userCode)) {
        toast({
          component: ToastificationContent,
          props: {
            title: proxy.$t("dmp.common.error"),
            text: proxy.$t("dmp.users.messages.userCodeOnlyAlphanumeric"),
            icon: "AlertTriangleIcon",
            variant: "danger",
          },
        });
        return;
      }

      const profilesRegex = /^[0-9]{1,3}$/;
      for (let i = 0; i < selectedUser.value.profiles.length; i++) {
        const profile = selectedUser.value.profiles[i];
        if (profile && profile.trim() && !profilesRegex.test(profile.trim())) {
          toast({
            component: ToastificationContent,
            props: {
              title: proxy.$t("dmp.common.error"),
              text: proxy.$t("dmp.users.messages.profileOnlyNumbers", { number: i + 1 }),
              icon: "AlertTriangleIcon",
              variant: "danger",
            },
          });
          return;
        }
      }

      // // Validar que al menos un perfil esté lleno
      const hasAtLeastOneProfile = selectedUser.value.profiles.some(
        (profile) => profile && profile.trim().length > 0
      );
      if (!hasAtLeastOneProfile) {
        toast({
          component: ToastificationContent,
          props: {
            title: proxy.$t("dmp.common.error"),
            text: proxy.$t("dmp.users.messages.atLeastOneProfile"),
            icon: "AlertTriangleIcon",
            variant: "danger",
          },
        });
        return;
      }

      const currentUsers = [...props.usersData];
      const userIndex = currentUsers.findIndex(
        (u) => u.userNumber === selectedUser.value.userNumber
      );

      if (
        userIndex !== -1 &&
        (!isEditingUser.value || userIndex !== editingUserIndex.value)
      ) {
        toast({
          component: ToastificationContent,
          props: {
            title: proxy.$t("dmp.common.error"),
            text: proxy.$t("dmp.users.messages.userNumberExists"),
            icon: "AlertTriangleIcon",
            variant: "danger",
          },
        });
        return;
      }

      const codeIndex = currentUsers.findIndex(
        (u) => u.userCode === selectedUser.value.userCode
      );

      if (
        codeIndex !== -1 &&
        (!isEditingUser.value || codeIndex !== editingUserIndex.value)
      ) {
        toast({
          component: ToastificationContent,
          props: {
            title: proxy.$t("dmp.common.error"),
            text: proxy.$t("dmp.users.messages.userCodeExists"),
            icon: "AlertTriangleIcon",
            variant: "danger",
          },
        });
        return;
      }

      if (isTemplateMode.value) {
        const filteredProfiles = selectedUser.value.profiles
          .filter((profile) => profile && profile.trim().length > 0)
          .map((profile) => profile.trim());

        const userItem = {
          pinCode: selectedUser.value.pinCode
            ? selectedUser.value.pinCode.toString()
            : "",
          profiles: filteredProfiles,
          userCode: selectedUser.value.userCode.trim(),
          userName: selectedUser.value.userName.trim().toUpperCase(),
          activeUser: selectedUser.value.activeUser ? "Y" : "N",
          userNumber: formatToFourDigits(selectedUser.value.userNumber, 1, 9999),
          description: selectedUser.value.description,
        };

        const list = [...(props.usersData || [])];
        const idx = list.findIndex((u) => u.userNumber === userItem.userNumber);
        if (idx !== -1) {
          userItem.id = list[idx].id != null ? list[idx].id : nextSequentialId(list);
          list.splice(idx, 1, userItem);
        } else {
          userItem.id = nextSequentialId(list);
          list.push(userItem);
        }

        const configList = [...(props.usersConfig.users || [])];
        const configIdx = configList.findIndex(
          (c) => String(c.id) === String(userItem.userNumber)
        );
        const configItem = {
          id: userItem.userNumber,
          description: userItem.description || "",
        };
        if (configIdx !== -1) {
          configList.splice(configIdx, 1, configItem);
        } else {
          configList.push(configItem);
        }

        users.value = list;

        emit("template-update", {
          "users": list,
          "config": { ...props.usersConfig, users: configList },
        });

        toast({
          component: ToastificationContent,
          props: {
            title: isEditingUser.value
              ? proxy.$t("dmp.users.messages.userUpdatedSuccess")
              : proxy.$t("dmp.users.messages.userAddedSuccess"),
            icon: "CheckIcon",
            variant: "success",
          },
        });
        selectedUser.value = null;
        isEditingUser.value = false;
        return;
      }

      isSavingUser.value = true;
      store.commit('dmp_panels/setOperationActive', true);
      try {
        const filteredProfiles = selectedUser.value.profiles
          .filter((profile) => profile && profile.trim().length > 0)
          .map((profile) => profile.trim());

        const userDataToSend = {
          userNumber: formatToFourDigits(selectedUser.value.userNumber, 1, 9999),
          userCode: selectedUser.value.userCode.trim(),
          pinCode: selectedUser.value.pinCode
            ? selectedUser.value.pinCode.toString()
            : "",
          userName: selectedUser.value.userName.trim().toUpperCase(),
          activeUser: selectedUser.value.activeUser,
          profiles: filteredProfiles,
          description: selectedUser.value.description,
        };

        const response = await store.dispatch("dmp_panels/setUser", {
          uuid: props.uuid,
          userData: userDataToSend,
        });

        if (response.success) {
          emit("operation-completed");

          toast({
            component: ToastificationContent,
            props: {
              title: isEditingUser.value
                ? proxy.$t("dmp.users.messages.userUpdatedSuccess")
                : proxy.$t("dmp.users.messages.userAddedSuccess"),
              icon: "CheckIcon",
              variant: "success",
            },
          });
          selectedUser.value = null;
          isEditingUser.value = false;
        } else {
          throw new Error(response.message || "Error saving user");
        }
      } catch (error) {
        console.error("Error saving user:", error);
        emit("operation-error", error);

        // Extraer mensaje específico de la API
        const apiMessage =
          (error.response && error.response.data && error.response.data.message) ||
          error.message ||
          proxy.$t("dmp.users.messages.userSaveError");

        toast({
          component: ToastificationContent,
          props: {
            title: proxy.$t("dmp.users.messages.userSaveError"),
            text: apiMessage,
            icon: "AlertTriangleIcon",
            variant: "danger",
          },
        });
      } finally {
        isSavingUser.value = false;
        store.commit('dmp_panels/setOperationActive', false);
      }
    };

    const formatProfile = (index) => {
      if (selectedUser.value && selectedUser.value.profiles[index]) {
        const formatted = formatToThreeDigits(selectedUser.value.profiles[index], 1, 999);
        selectedUser.value.profiles[index] = formatted;
      }
    };

    const generateRandomCode = () => {
      if (selectedUser.value) {
        const code = String(Math.floor(100000 + Math.random() * 900000));
        selectedUser.value.userCode = code;
      }
    };

    const generateRandomPin = () => {
      if (selectedUser.value) {
        selectedUser.value.pinCode = Math.floor(100000 + Math.random() * 900000);
      }
    };

    const formatUserNumber = () => {
      if (selectedUser.value && selectedUser.value.userNumber) {
        const formatted = formatToFourDigits(selectedUser.value.userNumber, 1, 9999);
        selectedUser.value.userNumber = formatted;
      }
    };

    // Sincroniza la tabla con los usuarios del panel/template. immediate:true
    // cubre también la carga inicial (antes la hacía loadUsers en onMounted).
    watch(
      () => props.usersData,
      (newData) => {
        if (newData) {
          users.value = [...newData];
        }
      },
      { deep: true, immediate: true }
    );

    const openModalUserCode = (user) => {
      userSelected.value = user;
      showModalUserCode.value = true;
    }

    const closeModalUserCode = () => {
      showModalUserCode.value = false;
      userSelected.value = null;
    }

    return {
      users,
      isLoadingUsers,
      isSavingUser,
      isDeletingUser,
      isSyncingUsers,
      selectedUser,
      isEditingUser,
      editingUserIndex,
      userFormObserver,
      userTableFields,
      showPinField,
      showModalUserCode,
      userSelected,

      addNewUser,
      onRowSelected,
      deleteUser,
      saveUser,
      formatProfile,
      formatUserNumber,
      generateRandomCode,
      generateRandomPin,
      openModalUserCode,
      closeModalUserCode,

      refFormObserver,
      getValidationState,
      profileSelectOptions,
      t,
      toast,
    };
  },
};
</script>

<style scoped lang="scss">
@import "@/assets/scss/variables/_variables.scss";

.card-header-custom {
  background-color: transparent;
}

.table-column,
.details-column {
  transition: all 0.3s ease-in-out;
}

.details-column {
  .card {
    position: sticky;
    top: 5rem;
  }
}

.dark-layout {
  .card-header-custom {
    background-color: transparent !important;
  }

  .btn-generate-code {
    color: #d0d2d6 !important;
    border-color: #d0d2d6 !important;

    &:hover {
      background-color: rgba(208, 210, 214, 0.12) !important;
    }
  }
}

.gap-1 {
  gap: 0.25rem;
}

.mb-75 {
  margin-bottom: 0.75rem;
}

.font-weight-bold {
  font-weight: 600;
}
</style>
